/**
 * findNearestSegment utility
 *
 * Provides segment-finding logic for node editing interactions.
 * Given a geometry and a click coordinate, finds the nearest line segment
 * and the closest point on that segment to the click position.
 *
 * **Validates: Requirement 16.2** – WHEN a Registered_User clicks on a line
 * segment, THE Geometry_Editor SHALL add a new node at that location.
 */

import type { Coordinate, Geometry } from '../services/GeometryEditor';
import { getNodes } from '../services/GeometryEditor';

/**
 * Result of finding the nearest segment to a point.
 */
export interface NearestSegmentResult {
  /** The global segment index (across all rings/parts) */
  segmentIndex: number;
  /** The closest point on the segment to the click coordinate */
  projectedPoint: Coordinate;
  /** The distance from the click coordinate to the projected point */
  distance: number;
}

/**
 * Represents a single line segment extracted from a geometry.
 */
interface Segment {
  /** Start point of the segment */
  start: Coordinate;
  /** End point of the segment */
  end: Coordinate;
  /** Global segment index */
  index: number;
}

/**
 * Extracts all line segments from a geometry.
 *
 * For LineString: segments connect consecutive coordinates.
 * For Polygon: segments connect consecutive coordinates in each ring
 *   (including the closing segment from last to first).
 * For Multi* types: segments are extracted from each part, with indices
 *   continuing sequentially.
 *
 * @param geometry - The GeoJSON geometry to extract segments from
 * @returns Array of segments with their global indices
 */
export function extractSegments(geometry: Geometry): Segment[] {
  const segments: Segment[] = [];
  let globalIndex = 0;

  switch (geometry.type) {
    case 'Point':
    case 'MultiPoint':
      // Points have no segments
      return [];

    case 'LineString': {
      const coords = geometry.coordinates as Coordinate[];
      for (let i = 0; i < coords.length - 1; i++) {
        segments.push({
          start: coords[i],
          end: coords[i + 1],
          index: globalIndex++,
        });
      }
      break;
    }

    case 'Polygon': {
      const rings = geometry.coordinates as Coordinate[][];
      for (const ring of rings) {
        // A closed ring has ring.length - 1 segments
        for (let i = 0; i < ring.length - 1; i++) {
          segments.push({
            start: ring[i],
            end: ring[i + 1],
            index: globalIndex++,
          });
        }
      }
      break;
    }

    case 'MultiLineString': {
      const lines = geometry.coordinates as Coordinate[][];
      for (const line of lines) {
        for (let i = 0; i < line.length - 1; i++) {
          segments.push({
            start: line[i],
            end: line[i + 1],
            index: globalIndex++,
          });
        }
      }
      break;
    }

    case 'MultiPolygon': {
      const polygons = geometry.coordinates as Coordinate[][][];
      for (const polygon of polygons) {
        for (const ring of polygon) {
          for (let i = 0; i < ring.length - 1; i++) {
            segments.push({
              start: ring[i],
              end: ring[i + 1],
              index: globalIndex++,
            });
          }
        }
      }
      break;
    }
  }

  return segments;
}

/**
 * Projects a point onto a line segment and returns the closest point on the segment.
 *
 * Uses the parametric form of the line: P = A + t*(B-A), where t is clamped to [0,1]
 * to stay within the segment bounds.
 *
 * @param point - The point to project
 * @param segStart - Start of the segment
 * @param segEnd - End of the segment
 * @returns The closest point on the segment to the given point
 */
export function projectPointOnSegment(
  point: Coordinate,
  segStart: Coordinate,
  segEnd: Coordinate
): Coordinate {
  const dx = segEnd[0] - segStart[0];
  const dy = segEnd[1] - segStart[1];

  // If segment is a zero-length point, return the start
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) {
    return [segStart[0], segStart[1]];
  }

  // Calculate parameter t for the projection
  const t = Math.max(
    0,
    Math.min(1, ((point[0] - segStart[0]) * dx + (point[1] - segStart[1]) * dy) / lengthSquared)
  );

  return [segStart[0] + t * dx, segStart[1] + t * dy];
}

/**
 * Calculates the Euclidean distance between two 2D coordinates.
 *
 * @param a - First coordinate
 * @param b - Second coordinate
 * @returns The distance between the two points
 */
export function distanceBetween(a: Coordinate, b: Coordinate): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  return Math.sqrt(dx * dx + dy * dy);
}

/**
 * Checks whether a coordinate is close to an existing node in the geometry.
 *
 * Used to determine if a click is on an existing vertex (in which case
 * we should select the node rather than add a new one).
 *
 * @param clickCoord - The click coordinate to check
 * @param geometry - The geometry containing existing nodes
 * @param tolerance - Distance threshold for considering a click "on" a node
 * @returns true if the click is within tolerance of any existing node
 */
export function isClickOnExistingNode(
  clickCoord: Coordinate,
  geometry: Geometry,
  tolerance: number
): boolean {
  const nodes: Coordinate[] = getNodes(geometry);

  for (const node of nodes) {
    if (distanceBetween(clickCoord, node) <= tolerance) {
      return true;
    }
  }
  return false;
}

/**
 * Finds the nearest line segment in a geometry to a given click coordinate.
 *
 * Iterates through all segments of the geometry, projects the click point
 * onto each segment, and returns the segment with the smallest distance.
 *
 * @param geometry - The GeoJSON geometry to search
 * @param clickCoordinate - The [x, y] coordinate of the click
 * @param tolerance - Maximum distance (in coordinate units) for a click to be
 *   considered "on" a segment. If no segment is within tolerance, returns null.
 * @returns The nearest segment result, or null if no segment is within tolerance
 */
export function findNearestSegment(
  geometry: Geometry,
  clickCoordinate: Coordinate,
  tolerance: number
): NearestSegmentResult | null {
  const segments = extractSegments(geometry);

  if (segments.length === 0) {
    return null;
  }

  let nearestResult: NearestSegmentResult | null = null;
  let minDistance = Infinity;

  for (const segment of segments) {
    const projected = projectPointOnSegment(clickCoordinate, segment.start, segment.end);
    const dist = distanceBetween(clickCoordinate, projected);

    if (dist < minDistance) {
      minDistance = dist;
      nearestResult = {
        segmentIndex: segment.index,
        projectedPoint: projected,
        distance: dist,
      };
    }
  }

  // Only return if within tolerance
  if (nearestResult && nearestResult.distance <= tolerance) {
    return nearestResult;
  }

  return null;
}
